import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

interface MaintenanceRecord {
  id: string
  device_id: string
  device_name: string
  device_type: string
  maintenance_type: 'preventive' | 'corrective' | 'calibration' | 'upgrade' | 'inspection'
  priority: 'low' | 'medium' | 'high' | 'critical'
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'overdue'
  title: string
  description: string
  scheduled_date: string
  started_date?: string
  completed_date?: string
  technician: string
  estimated_duration: number
  actual_duration?: number
  cost: number
  parts_used: MaintenancePart[]
  notes: string
  attachments: string[]
  next_maintenance_date?: string
  warranty_status: 'covered' | 'expired' | 'not_applicable'
  created_at: string
  updated_at: string
}

interface MaintenancePart {
  id: string
  name: string
  part_number: string
  quantity: number
  unit_cost: number
  total_cost: number
  supplier: string
}

interface MaintenanceSchedule {
  id: string
  device_id: string
  device_name: string
  maintenance_type: string
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'semi_annual' | 'annual'
  last_performed: string
  next_due: string
  is_overdue: boolean
  days_overdue: number
  assigned_technician: string
}

const MaintenanceRecords: React.FC = () => {
  const [records, setRecords] = useState<MaintenanceRecord[]>([])
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([])
  const [selectedRecord, setSelectedRecord] = useState<MaintenanceRecord | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  })
  const [activeTab, setActiveTab] = useState<'records' | 'schedule' | 'overdue'>('records')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)

  const [formData, setFormData] = useState({
    device_id: '',
    maintenance_type: 'preventive' as const,
    priority: 'medium' as const,
    title: '',
    description: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    technician: '',
    estimated_duration: 60,
    cost: 0,
    notes: ''
  })

  const fetchRecords = async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams({
        search: searchTerm,
        status: statusFilter,
        type: typeFilter,
        start_date: dateRange.start,
        end_date: dateRange.end
      })

      const response = await fetch(`/api/v1/devices/maintenance/records/?${params}`)
      if (response.ok) {
        const data = await response.json()
        setRecords(data.results || data)
      } else {
        toast.error('Failed to fetch maintenance records')
      }
    } catch (error) {
      console.error('Error fetching records:', error)
      toast.error('Error fetching records')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSchedules = async () => {
    try {
      const response = await fetch('/api/v1/devices/maintenance/schedules/')
      if (response.ok) {
        const data = await response.json()
        setSchedules(data.results || data)
      }
    } catch (error) {
      console.error('Error fetching schedules:', error)
    }
  }

  useEffect(() => {
    fetchRecords()
    fetchSchedules()
  }, [searchTerm, statusFilter, typeFilter, dateRange])

  const createMaintenanceRecord = async () => {
    try {
      const response = await fetch('/api/v1/devices/maintenance/records/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast.success('Maintenance record created successfully')
        setShowCreateModal(false)
        fetchRecords()
        fetchSchedules()
      } else {
        toast.error('Failed to create maintenance record')
      }
    } catch (error) {
      console.error('Error creating record:', error)
      toast.error('Error creating record')
    }
  }

  const updateMaintenanceStatus = async (recordId: string, status: string) => {
    try {
      const response = await fetch(`/api/v1/devices/maintenance/records/${recordId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      })

      if (response.ok) {
        toast.success('Status updated successfully')
        fetchRecords()
      } else {
        toast.error('Failed to update status')
      }
    } catch (error) {
      console.error('Error updating status:', error)
      toast.error('Error updating status')
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      overdue: 'bg-red-200 text-red-900'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      preventive: 'bg-green-100 text-green-800',
      corrective: 'bg-red-100 text-red-800',
      calibration: 'bg-purple-100 text-purple-800',
      upgrade: 'bg-blue-100 text-blue-800',
      inspection: 'bg-orange-100 text-orange-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    }
    return colors[priority] || 'bg-gray-100 text-gray-800'
  }

  const overdueSchedules = schedules.filter(s => s.is_overdue)
  // const upcomingSchedules = schedules.filter(s => !s.is_overdue && new Date(s.next_due) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Maintenance Records</h1>
          <p className="text-gray-600">Track device maintenance, repairs, and schedules</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            New Maintenance
          </button>
          <button className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">
            Export Report
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {[
          { label: 'Total Records', value: records.length, color: 'blue', icon: '📋' },
          { label: 'In Progress', value: records.filter(r => r.status === 'in_progress').length, color: 'yellow', icon: '⚙️' },
          { label: 'Completed', value: records.filter(r => r.status === 'completed').length, color: 'green', icon: '✅' },
          { label: 'Overdue', value: overdueSchedules.length, color: 'red', icon: '🚨' },
          { label: 'Total Cost', value: `$${records.reduce((sum, r) => sum + r.cost, 0).toLocaleString()}`, color: 'purple', icon: '💰', isText: true }
        ].map((stat) => (
          <div key={stat.label} className="bg-white p-6 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className={`text-2xl font-bold text-gray-900 ${stat.isText ? 'text-lg' : ''}`}>
                  {stat.value}
                </p>
              </div>
              <div className="text-2xl">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'records', label: `Records (${records.length})` },
            { key: 'schedule', label: `Schedule (${schedules.length})` },
            { key: 'overdue', label: `Overdue (${overdueSchedules.length})` }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search records..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="preventive">Preventive</option>
              <option value="corrective">Corrective</option>
              <option value="calibration">Calibration</option>
              <option value="upgrade">Upgrade</option>
              <option value="inspection">Inspection</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Records Tab */}
      {activeTab === 'records' && (
        <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device / Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type / Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Technician
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      Loading maintenance records...
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      No maintenance records found.
                    </td>
                  </tr>
                ) : (
                  records.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{record.device_name}</div>
                          <div className="text-sm text-gray-500">{record.title}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(record.maintenance_type)}`}>
                            {record.maintenance_type.toUpperCase()}
                          </span>
                          <br />
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(record.priority)}`}>
                            {record.priority.toUpperCase()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={record.status}
                          onChange={(e) => updateMaintenanceStatus(record.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full border-0 ${getStatusColor(record.status)}`}
                        >
                          <option value="scheduled">Scheduled</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.technician}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>
                          <div>Scheduled: {new Date(record.scheduled_date).toLocaleDateString()}</div>
                          {record.completed_date && (
                            <div>Completed: {new Date(record.completed_date).toLocaleDateString()}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${record.cost.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedRecord(record)
                            setShowDetailModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <div className="space-y-4">
          {schedules.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border">
              <p className="text-gray-500">No maintenance schedules found</p>
            </div>
          ) : (
            schedules.map((schedule) => (
              <div key={schedule.id} className="bg-white rounded-lg border p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{schedule.device_name}</h3>
                    <p className="text-sm text-gray-600 capitalize">{schedule.maintenance_type.replace('_', ' ')}</p>
                    <p className="text-xs text-gray-500">Frequency: {schedule.frequency.replace('_', ' ')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      Next Due: {new Date(schedule.next_due).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      Technician: {schedule.assigned_technician}
                    </p>
                    {schedule.is_overdue && (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        {schedule.days_overdue} days overdue
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Overdue Tab */}
      {activeTab === 'overdue' && (
        <div className="space-y-4">
          {overdueSchedules.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border">
              <p className="text-gray-500">No overdue maintenance found</p>
            </div>
          ) : (
            overdueSchedules.map((schedule) => (
              <div key={schedule.id} className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-red-900">{schedule.device_name}</h3>
                    <p className="text-sm text-red-700 capitalize">{schedule.maintenance_type.replace('_', ' ')}</p>
                    <p className="text-xs text-red-600">
                      Due: {new Date(schedule.next_due).toLocaleDateString()} ({schedule.days_overdue} days ago)
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm">
                      Schedule Now
                    </button>
                    <button className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 text-sm">
                      Reassign
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create Maintenance Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Schedule Maintenance</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Device</label>
                <input
                  type="text"
                  value={formData.device_id}
                  onChange={(e) => setFormData({...formData, device_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter device ID"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Maintenance Type</label>
                <select
                  value={formData.maintenance_type}
                  onChange={(e) => setFormData({...formData, maintenance_type: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="preventive">Preventive</option>
                  <option value="corrective">Corrective</option>
                  <option value="calibration">Calibration</option>
                  <option value="upgrade">Upgrade</option>
                  <option value="inspection">Inspection</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Scheduled Date</label>
                <input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Technician</label>
                <input
                  type="text"
                  value={formData.technician}
                  onChange={(e) => setFormData({...formData, technician: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Duration (minutes)</label>
                <input
                  type="number"
                  value={formData.estimated_duration}
                  onChange={(e) => setFormData({...formData, estimated_duration: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  min="15"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={createMaintenanceRecord}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Schedule Maintenance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRecord && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Maintenance Details - {selectedRecord.title}
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Basic Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Device:</span> {selectedRecord.device_name}</p>
                    <p><span className="font-medium">Type:</span> {selectedRecord.maintenance_type}</p>
                    <p><span className="font-medium">Priority:</span> {selectedRecord.priority}</p>
                    <p><span className="font-medium">Technician:</span> {selectedRecord.technician}</p>
                    <p><span className="font-medium">Status:</span>
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusColor(selectedRecord.status)}`}>
                        {selectedRecord.status.toUpperCase()}
                      </span>
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Timeline</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Scheduled:</span> {new Date(selectedRecord.scheduled_date).toLocaleDateString()}</p>
                    {selectedRecord.started_date && (
                      <p><span className="font-medium">Started:</span> {new Date(selectedRecord.started_date).toLocaleDateString()}</p>
                    )}
                    {selectedRecord.completed_date && (
                      <p><span className="font-medium">Completed:</span> {new Date(selectedRecord.completed_date).toLocaleDateString()}</p>
                    )}
                    <p><span className="font-medium">Duration:</span> {selectedRecord.estimated_duration} min (estimated)
                      {selectedRecord.actual_duration && ` / ${selectedRecord.actual_duration} min (actual)`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Cost Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Total Cost:</span> ${selectedRecord.cost.toLocaleString()}</p>
                    <p><span className="font-medium">Warranty:</span> {selectedRecord.warranty_status.replace('_', ' ')}</p>
                    {selectedRecord.parts_used.length > 0 && (
                      <div>
                        <p className="font-medium">Parts Used:</p>
                        <ul className="ml-4 space-y-1">
                          {selectedRecord.parts_used.map((part, index) => (
                            <li key={index}>
                              {part.name} (${part.total_cost})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                    {selectedRecord.description}
                  </p>
                </div>
              </div>
            </div>

            {selectedRecord.notes && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  {selectedRecord.notes}
                </p>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MaintenanceRecords